import trackMixpanelEvent from '@/common/utils/mixpanel';
import dialogTexts from '@/game/GameComponents/GameData/dialog-texts.json';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import { getStudent } from '@/game/GameComponents/PhaserGame/GameInstance';
import { ResponsivenessManager } from '@/game/GameComponents/PhaserGame/Managers/ResponsivenessManager';
import { getSceneManager } from '@/game/GameComponents/PhaserGame/Managers/SceneManager';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import {
  getAnchorPlugin,
  getNextGame,
} from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import DefaultButton from '@/game/prefabs/GeneralUI/Buttons/DefaultButton';
import DefaultDenyButton from '@/game/prefabs/GeneralUI/Buttons/DefaultDenyButton';
import HUDButton from '@/game/prefabs/GeneralUI/Buttons/HUDButton';
import WorldNode, { NodeStatus } from '@/game/prefabs/World/WorldNode';
import { NPCDialogueConfig } from '@/game/prefabs/WorldUI/NpcDialogue/npcDialogueTypes';
import Duel from '@/game/scenes/DuelScenes/Duel';
import DuelUI from '@/game/scenes/DuelScenes/DuelUI';
import DuelWordCreationPanel from '@/game/scenes/DuelScenes/DuelWordCreationPanel';
import RiposteUI from '@/game/scenes/DuelScenes/RiposteUI';
import npcDialogue from './npcDialogue';

export class NpcDialogueController {
  constructor(private npcDialogue: npcDialogue) {
    this.awake();
  }

  getResponsiveElements(key: keyof npcDialogue) {
    return ResponsivenessManager.GetAllResponsiveComponentsFromContainer(
      this.npcDialogue,
      key,
    );
  }

  unsubscribeFromStore?: () => void;
  node: WorldNode | null = null; // Allow the node to be null

  async configure(config: NPCDialogueConfig) {
    const { npcDialogue } = this;
    npcDialogue.setVisible(true);
    PhaserAudioManager.playSFX(npcDialogue.scene, 'npc_dialogue_open');
    this.node = config.node;

    // hide duel button if disabled
    const isDuelsEnabled = getStudent().settings?.duelsEnabled ?? true;
    this.getResponsiveElements('duelButton').forEach(
      (duelButton: HUDButton) => {
        duelButton.setVisible(isDuelsEnabled);
      },
    );

    const awaitingGameText =
      dialogTexts[getNextGame() as keyof typeof dialogTexts];
    const questionTextContent =
      this.node?.npcStatus === NodeStatus.COMPLETE
        ? 'Thanks for your help!'
        : awaitingGameText;

    this.getResponsiveElements('acceptButton').forEach(
      (acceptButton: DefaultButton) => {
        acceptButton.setVisible(this.node?.npcStatus !== NodeStatus.COMPLETE);
      },
    );

    this.getResponsiveElements('cancelButton').forEach(
      (cancelButton: DefaultDenyButton) => {
        cancelButton.controller.setText(
          this.node?.npcStatus === NodeStatus.COMPLETE ? 'OK' : 'No',
        );
      },
    );

    this.getResponsiveElements('dialogueText').forEach(
      (dialogueText: Phaser.GameObjects.BitmapText) => {
        dialogueText.setText(questionTextContent);
      },
    );
  }

  awake() {
    const { npcDialogue } = this;
    this.getResponsiveElements('acceptButton').forEach(
      (acceptButton: DefaultButton) => {
        acceptButton.controller.setSubmissionCallback(() => {
          if (this.node) {
            EventBus.emit(GAME_EVENTS.MINIGAME_STARTED, this.node);
          } else {
            console.error('No node available to start game'); // Log or handle the error as necessary
          }
          npcDialogue.setVisible(false);
        });
      },
    );

    this.getResponsiveElements('cancelButton').forEach(
      (cancelButton: DefaultDenyButton) => {
        cancelButton.controller.setSubmissionCallback(() => {
          // Logic for when cancel is clicked
          npcDialogue.setVisible(false);
          PhaserAudioManager.playSFX(npcDialogue.scene, 'panel-close');
          EventBus.emit(GAME_EVENTS.NPC_MODAL_CLOSED, this.node);
        });
      },
    );

    this.getResponsiveElements('duelButton').forEach(
      (duelButton: HUDButton) => {
        duelButton.setSubmissionCallback(() => {
          // Logic to start the duel
          if (this.node) {
            npcDialogue.setVisible(false);
            getSceneManager()?.launchScene('Duel', Duel, {
              type: 'gameplay',
              subScenes: [
                {
                  key: 'DuelUI',
                  scene: DuelUI,
                  type: 'gameplay',
                },
                {
                  key: 'DuelWordCreationPanel',
                  scene: DuelWordCreationPanel,
                  type: 'gameplay',
                },
                {
                  key: 'RiposteUI',
                  scene: RiposteUI,
                  type: 'gameplay',
                },
              ],
              data: { node: this.node },
            });
            //mixpanel
            trackMixpanelEvent('Duel Started');
          } else {
            console.error('No node available to start duel'); // Log or handle the error as necessary
          }
        });
      },
    );

    npcDialogue.setVisible(false);

    this.initializeResponsiveness();
  }

  initializeResponsiveness() {
    const { npcDialogue } = this;

    // Breakpoints.
    ResponsivenessManager.InitializeResponsiveBreakpoints({
      scene: npcDialogue.scene,
      desktopView: npcDialogue.desktopView,
      mobileView: npcDialogue.mobileView,
    });

    // Responsiveness.
    const anchor = getAnchorPlugin(npcDialogue.scene);
    anchor.add(npcDialogue, {
      left: '0%',
      top: '0%',
    });
    anchor.add(npcDialogue.dialogueContainer, {
      centerX: '50%',
      bottom: '100%-164',
    });
    anchor.add(npcDialogue.dialogueContainer_m, {
      centerX: '50%',
      bottom: '100%-128',
    });

    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      npcDialogue.dialogueContainer,
    );
  }
}
